import { Helmet } from 'react-helmet-async';

interface StructuredDataProps {
  type: 'homepage' | 'tip' | 'article' | 'search' | 'archive';
  data?: {
    title?: string;
    description?: string;
    url?: string;
    publishedAt?: string;
    category?: string;
    rating?: number;
    ratingCount?: number;
    imageUrl?: string;
    videoUrl?: string;
  };
}

export const StructuredData = ({ type, data = {} }: StructuredDataProps) => {
  const baseUrl = "https://d5a54847-8b45-4634-b225-528aa06bb2c6.lovableproject.com";
  
  const getStructuredData = () => {
    const baseStructure = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "url": data.url || baseUrl,
      "name": data.title || "1% A Day - Daily Self-Improvement Tips",
      "description": data.description || "Transform your life with daily practical tips for continuous improvement.",
      "isPartOf": {
        "@type": "WebSite",
        "name": "1% A Day",
        "url": baseUrl
      },
      "inLanguage": "en-US",
      "copyrightYear": "2024",
      "author": {
        "@type": "Organization",
        "name": "1% A Day"
      }
    };

    switch (type) {
      case 'homepage':
        return {
          ...baseStructure,
          "@type": "CollectionPage",
          "mainEntity": {
            "@type": "ItemList",
            "name": "Daily Self-Improvement Tips",
            "description": "Collection of practical tips for continuous personal development",
            "numberOfItems": "500+"
          },
          "about": [
            {
              "@type": "Thing",
              "name": "Self-improvement",
              "description": "Personal development and growth strategies"
            },
            {
              "@type": "Thing", 
              "name": "Productivity",
              "description": "Methods to increase efficiency and effectiveness"
            },
            {
              "@type": "Thing",
              "name": "Habit Formation",
              "description": "Building positive habits for long-term success"
            }
          ],
          "potentialAction": [
            {
              "@type": "ReadAction",
              "target": `${baseUrl}/archive`,
              "name": "Browse All Tips"
            },
            {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": `${baseUrl}/search?q={search_term_string}`
              },
              "query-input": "required name=search_term_string"
            }
          ]
        };

      case 'tip':
      case 'article':
        return {
          ...baseStructure,
          "@type": "Article",
          "headline": data.title,
          "description": data.description,
          "datePublished": data.publishedAt,
          "dateModified": data.publishedAt,
          "articleSection": data.category || "Self-improvement",
          "wordCount": data.description?.length || 0,
          "image": data.imageUrl,
          "publisher": {
            "@type": "Organization",
            "name": "1% A Day",
            "logo": {
              "@type": "ImageObject",
              "url": "https://lovable.dev/opengraph-image-p98pqg.png"
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": data.url
          },
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [".tip-content", ".article-content"]
          },
          ...(data.rating && data.ratingCount && {
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": data.rating,
              "reviewCount": data.ratingCount,
              "bestRating": 5,
              "worstRating": 1
            }
          }),
          ...(data.videoUrl && {
            "video": {
              "@type": "VideoObject",
              "name": data.title,
              "description": data.description,
              "contentUrl": data.videoUrl,
              "embedUrl": data.videoUrl.includes('youtube') 
                ? data.videoUrl.replace('watch?v=', 'embed/') 
                : data.videoUrl,
              "uploadDate": data.publishedAt,
              "duration": "PT2M",
              "thumbnailUrl": data.imageUrl
            }
          })
        };

      case 'search':
        return {
          ...baseStructure,
          "@type": "SearchResultsPage",
          "mainEntity": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${baseUrl}/search?q={search_term_string}`
            },
            "query-input": "required name=search_term_string"
          }
        };

      case 'archive':
        return {
          ...baseStructure,
          "@type": "CollectionPage",
          "mainEntity": {
            "@type": "ItemList",
            "name": "All Self-Improvement Tips",
            "description": "Complete archive of daily tips for personal development"
          },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": baseUrl
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Archive",
                "item": `${baseUrl}/archive`
              }
            ]
          }
        };

      default:
        return baseStructure;
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(getStructuredData())}
      </script>
    </Helmet>
  );
};